import { type FC, type MouseEvent } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { useShopfrontDispatch } from "../../../../_Bootstrap/hooks";
import { showDialog } from "../../../../Actions/Dialogs/DialogActions";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Duration from "../../../Duration/Duration";
import { ImagePreview } from "../ImagePreview";
import { type MediaFileType, MediaType } from "../MediaUtilities";
import { VideoPreview } from "../VideoPreview";
import { FileConnectionList } from "./FileConnectionList";
import { getSizeString } from "./MediaDetails";

interface Props {
    file: MediaFileType;
    small?: boolean;
}

export const FileDetails: FC<Props> = ({ file, small }) => {
    const dispatch = useShopfrontDispatch();

    if(small) {
        let icon: IconProp;

        if(file.type === MediaType.Image) {
            icon = "file-image";
        } else if(file.type === MediaType.Video) {
            icon = "file-video";
        } else {
            icon = "file-exclamation";
        }

        return (
            <div className="item" key={file.name}>
                <div className="media">
                    <FontAwesomeLight icon={icon} />
                </div>
                <div className="details">
                    <div className="name">{file.name}</div>
                </div>
            </div>
        );
    }

    const showImagePreview = (e: MouseEvent<HTMLImageElement>): void => {
        e.stopPropagation();

        if(!file) {
            return;
        }

        dispatch(showDialog(
            file.name,
            <ImagePreview
                url={file.url}
                name={file.name}
            />,
            "information",
            false,
            false,
            true,
            false
        ));
    };

    let media;

    if(file.type === MediaType.Image) {
        media = <img
            src={file.url}
            alt={file.name}
            onClick={showImagePreview}
        />;
    } else if(file.type === MediaType.Video) {
        media = <VideoPreview src={file.url} />;
    } else {
        media = "Invalid media type";
    }

    return (
        <div className="item" key={file.name}>
            <div className="media">
                {media}
            </div>
            <div className="details">
                <div className="name">{file.name}</div>
                {file.createdAt && (
                    <div className="created-at">
                        Created{" "}
                        <Duration
                            key={file.uuid}
                            timestamp={file.createdAt}
                        />
                    </div>
                )}
                {file.updatedAt && (
                    <div className="updated-at">
                        Modified
                        <Duration
                            key={file.uuid}
                            timestamp={file.updatedAt}
                        />
                    </div>
                )}
                <div className="size">
                    <FontAwesomeLight icon="hdd" />
                    {getSizeString(file.size)}
                </div>
                <div className="file-type">
                    <FontAwesomeLight icon="file-alt" />
                    {file.extension}
                </div>
                {file.connections.length > 0 && (
                    <FileConnectionList connections={file.connections} />
                )}
            </div>
        </div>
    );
};
